<template>
	<v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition" scrollable @keydown.esc="closeDialog">
		<v-card>
			<v-toolbar card dark color="primary">
				<v-btn icon dark @click.native="closeDialog">
					<v-icon>close</v-icon>
				</v-btn>
				<v-toolbar-title>{{ title }}</v-toolbar-title>
				<v-spacer></v-spacer>
				<v-toolbar-items>
					<v-btn :loading="isSaving" :disabled="isSaving" dark flat @click="onSave"> Save <span slot="loader">Saving...</span> </v-btn>
				</v-toolbar-items>
			</v-toolbar>
			<v-card-text style="height: 100%">
				<v-form>
					<v-container grid-list-xl fluid>
						<v-layout row wrap>
							<!-- Core Information Section -->
							<v-flex lg6>
								<v-card class="mb-4">
									<v-toolbar flat dense>
										<v-toolbar-title class="subheading">Core Information</v-toolbar-title>
										<v-spacer></v-spacer>
									</v-toolbar>
									<v-card-text class="text-xs-left">
										<v-text-field v-model="form.name" ref="name" :error-messages="nameErrors" label="Name"></v-text-field>
										<v-textarea v-model="form.description" :error-messages="descriptionErrors" label="Description (optional)"></v-textarea>
										<h4>Grant permissions for:</h4>
										<!-- <v-checkbox label="All employees"></v-checkbox> -->
										<multi-select :options="availableScopes" :selectedOptions="selectedScopes" placeholder="Select members" @update:selected-options="updateScopes" @on-leave="onLeaveScopeSelector"></multi-select>
									</v-card-text>
								</v-card>
							</v-flex>

							<!-- Permissions Section -->
							<v-flex lg6>
								<v-card class="mb-4">
									<v-toolbar flat dense>
										<v-toolbar-title class="subheading">Permissions</v-toolbar-title>
										<v-spacer></v-spacer>
									</v-toolbar>
									<v-card-text>
										<div v-for="feature in availableFeatures" :key="feature.featureId">
											<feature-permission-selector :feature="feature" :feature-permission="findFeaturePermission(feature.featureId)" @update-feature-permission="updateFeaturePermission"></feature-permission-selector>
										</div>
									</v-card-text>
								</v-card>
							</v-flex>
						</v-layout>
					</v-container>
				</v-form>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script>
	import { mapGetters } from 'vuex';
	import { validationMixin } from 'vuelidate';
	import { required } from 'vuelidate/lib/validators';
	import FeaturePermissionSelector from '@/components/security/FeaturePermissionSelector';
	import TagSelector from '@/components/tags/TagSelector';
	import MultiSelect from '@/components/common/MultiSelect';

	export default {
		components: {
			FeaturePermissionSelector,
			TagSelector,
			MultiSelect
		},
		mixins: [validationMixin],
		props: {
			dialog: {
				required: true
			},
			securityGroup: {
				required: false
			}
		},
		data() {
			return {
				form: {
					id: null,
					name: null,
					description: null,
					featurePermissions: [],
					scopes: []
				},
				selectedScopes: []
			};
		},
		computed: {
			...mapGetters({
				availableFeatures: 'privileges/availableFeatures',
				departments: 'departments/departmentsWithEmployeeCount',
				employees: 'employees/employeesMiniActiveOnly',
				isSaving: 'privileges/isSavingSecurityGroup'
			}),
			title() {
				return this.securityGroup ? 'Edit Permission Group' : 'Create Permission Group';
			},
			nameErrors() {
				const errors = [];
				if (!this.$v.form.name.$dirty) return errors;
				!this.$v.form.name.required && errors.push('Name is required.');
				return errors;
			},
			descriptionErrors() {
				return [];
			},
			isEdit() {
				return this.securityGroup != null;
			},
			availableScopes() {
				const departmentsMapped = this.departments.map(d => ({
					type: 'Department',
					id: d.id,
					text: d.name,
					icon: 'mdi-account-group',
					suffix: `(${d.employeeCount} employees)`
				}));

				const employeesMapped = this.employees.map(e => ({
					type: 'Employee',
					id: e.id,
					text: e.fullName,
					icon: 'mdi-account'
				}));

				return [...departmentsMapped, ...employeesMapped];
			}
		},
		watch: {
			dialog(newVal) {
				if (newVal) this.$nextTick(() => (this.$refs.name ? this.$refs.name.focus() : null));
			},
			securityGroup: {
				immediate: true,
				deep: true,
				handler(newVal) {
					if (!newVal) {
						this.form = this.getBlankForm();
						this.selectedScopes = [];
						return;
					}
					this.form = { ...this.getBlankForm(), ...newVal };

					// Map the existing scopes to the availableScopes format
					this.selectedScopes = this.mapScopesToAvailableScopes(this.form.scopes);
				}
			}
		},
		methods: {
			/**
			 * Handles saving the security group.
			 */
			async onSave() {
				this.$v.form.$touch();
				if (this.$v.form.$invalid) return;

				const savePayload = {
					...this.form
				};

				if (this.isEdit) {
					// Update security group
					const result = await this.$store.dispatch('privileges/updateSecurityGroup', savePayload);
					if (result.isSuccess) {
						this.closeDialog();
					}
				} else {
					// Create security group
					const result = await this.$store.dispatch('privileges/createSecurityGroup', savePayload);
					if (result.isSuccess) {
						this.closeDialog();
					}
				}
			},

			/**
			 * Closes the dialog and resets the form.
			 */
			closeDialog() {
				this.$v.form.$reset();
				this.selectedScopes = [];
				this.form = this.getBlankForm();
				this.$emit('closeDialog');
			},

			/**
			 * Returns a blank form structure.
			 */
			getBlankForm() {
				return {
					id: null,
					name: null,
					description: null,
					featurePermissions: [],
					scopes: []
				};
			},

			/**
			 * Finds the feature permission for a given feature ID.
			 */
			findFeaturePermission(featureId) {
				return (
					this.form.featurePermissions.find(fp => fp.featureId === featureId) || {
						featureId,
						actions: []
					}
				);
			},

			/**
			 * Updates the feature permissions when emitted by a child component.
			 */
			updateFeaturePermission(updatedPermission) {
				const updatedPermissions = [...this.form.featurePermissions];
				const index = updatedPermissions.findIndex(fp => fp.featureId === updatedPermission.featureId);

				if (index === -1) {
					// Add new permission
					updatedPermissions.push(updatedPermission);
				} else {
					// Update existing permission
					updatedPermissions.splice(index, 1, updatedPermission);
				}

				this.form.featurePermissions = updatedPermissions;
			},
			onLeaveScopeSelector() {
				this.$refs.focusAfterLocationSelector.focus();
			},
			updateScopes(newScopes) {
				this.selectedScopes = newScopes;

				// Group the scopes by type
				const groupedScopes = newScopes.reduce((acc, scope) => {
					// Check if the type already exists in the accumulator
					const existingGroup = acc.find(group => group.type === scope.type);

					if (existingGroup) {
						// Add the ID to the existing group's values
						existingGroup.values.push(scope.id);
					} else {
						// Create a new group for this type
						acc.push({
							type: scope.type,
							values: [scope.id]
						});
					}

					return acc;
				}, []);

				this.form.scopes = groupedScopes;
			},
			/**
			 * Transforms grouped scopes back to the availableScopes format.
			 * This is useful when loading an existing security group into the form.
			 * @param {Array} groupedScopes - Array of grouped scopes from the security group.
			 * @returns {Array} - Transformed scopes in the availableScopes format.
			 */
			mapScopesToAvailableScopes(groupedScopes) {
				// Create a map of departments and employees for quick lookup
				const departmentMap = new Map(this.departments.map(dept => [dept.id, dept]));
				const employeeMap = new Map(this.employees.map(emp => [emp.id, emp]));

				// Iterate over the grouped scopes
				return groupedScopes.flatMap(group => {
					if (group.type === 'Department') {
						return group.values
							.map(deptId => departmentMap.get(deptId)) // Lookup department by ID
							.filter(Boolean) // Remove null/undefined results
							.map(department => ({
								type: 'Department',
								id: department.id,
								text: department.name,
								icon: 'mdi-account-group',
								suffix: `(${department.employeeCount} employees)`
							}));
					} else if (group.type === 'Employee') {
						return group.values
							.map(empId => employeeMap.get(empId)) // Lookup employee by ID
							.filter(Boolean) // Remove null/undefined results
							.map(employee => ({
								type: 'Employee',
								id: employee.id,
								text: employee.fullName,
								icon: 'mdi-account'
							}));
					}
					return []; // If type doesn't match, return empty array
				});
			}
		},
		validations: {
			form: {
				name: { required }
			}
		}
	};
</script>
